import { Component, computed, inject, input, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
    CeButtonComponent,
    CeIconComponent,
    CeParameterListImports,
    CeSnackbarService,
    CeSnackbarState,
    CeSpinningLoaderComponent,
} from '@kempower-nx-workspace/shared/kp-design-system';
import { FindReceiptsResponseItem } from '@kempoweroyj/ngx-receipts-api-client';
import {
    convertDurationMsToDisplay,
    convertIsoDateToDisplay,
    convertSessionStartedToDisplay,
} from '../../utils/date-utils';
import { catchError, of } from 'rxjs';

@Component({
    selector: 'lib-found-receipt',
    standalone: true,
    imports: [CommonModule, CeParameterListImports, CeIconComponent, CeButtonComponent, CeSpinningLoaderComponent],
    templateUrl: './found-receipt.component.html',
    styleUrl: './found-receipt.component.scss',
})
export class FoundReceiptComponent {
    receipt = input<FindReceiptsResponseItem | null>(null);
    isDownloading = signal<boolean>(false);

    readonly #http = inject(HttpClient);
    readonly #snackbar = inject(CeSnackbarService);

    displayDate = computed(() => convertIsoDateToDisplay(this.receipt()?.date));
    displaySessionStarted = computed(() =>
        convertSessionStartedToDisplay(this.receipt()?.sessionStarted, this.receipt()?.stationTimezone)
    );
    displayDuration = computed(() => convertDurationMsToDisplay(this.receipt()?.durationMs));

    downloadReceipt() {
        if (!this.receipt() || !this.receipt()?.downloadUrl) {
            return;
        }

        this.isDownloading.set(true);
        const downloadUrl = `${this.receipt()!.downloadUrl}&asPdf=true`;
        const filename = `receipt-${this.receipt()?.txId || 'download'}.pdf`;

        this.#downloadFileFromUrl(downloadUrl, filename);
    }

    #downloadFileFromUrl(url: string, filename: string) {
        // Create a hidden link element and trigger download
        // This approach handles redirects automatically
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        link.rel = 'noopener noreferrer';

        document.body.appendChild(link);

        link.addEventListener('click', () => {
            setTimeout(() => {
                this.isDownloading.set(false);
                document.body.removeChild(link);
            }, 1000);
        });

        link.addEventListener('error', () => {
            this.#snackbar.showSnackbar({
                message: 'Error downloading the receipt',
                state: CeSnackbarState.ERROR,
                icon: 'error',
                showCloseButton: true,
            });
            this.isDownloading.set(false);
            document.body.removeChild(link);
        });

        link.click();
    }
}
